import { TooltipContentProps } from '@radix-ui/react-tooltip';

import React, { useEffect, useMemo } from 'react';

import {
	Tooltip as ShadcnTooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';

import { cn } from '@/utils/styles';

interface TooltipProps extends Omit<TooltipContentProps, 'content'> {
	text?: string;
	content?: React.ReactNode;
	className?: string;
	children: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	triggerProps?: React.ComponentProps<typeof TooltipTrigger>;
	delayDuration?: number;
	mobileDuration?: number;
	showArrow?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
	text,
	content,
	className,
	children,
	open,
	onOpenChange,
	triggerProps = {},
	delayDuration = 50,
	mobileDuration = 1500,
	showArrow = true,
	...contentProps
}) => {
	const [isTooltipOpen, setIsTooltipOpen] = React.useState(open);
	const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

	const tooltipContent = content ?? text;

	const handleTouchStart = () => {
		// Clear any existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		setIsTooltipOpen(true);
	};

	const handleTouchEnd = () => {
		// Set a timeout to close the tooltip after the specified duration
		timeoutRef.current = setTimeout(() => {
			// setIsTooltipOpen(false);
		}, mobileDuration);
	};

	useEffect(() => {
		if (typeof open === 'boolean') setIsTooltipOpen(open);
	}, [open]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const effectiveOpen = useMemo(() => {
		return open ?? isTooltipOpen;
	}, [open, isTooltipOpen]);

	return (
		<TooltipProvider>
			<ShadcnTooltip
				open={effectiveOpen}
				onOpenChange={(o) => {
					onOpenChange?.(o);
					setIsTooltipOpen(o);
				}}
				delayDuration={delayDuration}
			>
				<TooltipTrigger
					asChild
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
					{...triggerProps}
				>
					{children}
				</TooltipTrigger>
				{tooltipContent ? (
					<TooltipContent
						{...contentProps}
						showArrow={showArrow}
						className={cn('max-w-full text-pretty md:max-w-[300px]', className)}
					>
						{tooltipContent}
					</TooltipContent>
				) : null}
			</ShadcnTooltip>
		</TooltipProvider>
	);
};
